import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import Logo from '@/components/Logo';
import { Mail, ArrowRight, Loader2, CheckCircle, Lock } from 'lucide-react';
import { toast } from 'sonner';

const ForgotPassword = () => {
  const navigate = useNavigate();
  const { forgotPassword, resetPassword } = useAuth();
  const [step, setStep] = useState(1); // 1: email, 2: otp, 3: new password, 4: success
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const handleSendOTP = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error('Veuillez entrer votre adresse email');
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast.error('Veuillez entrer une adresse email valide');
      return;
    }

    setLoading(true);
    try {
      await forgotPassword(email);
      toast.success('Si cet email existe, un code a ete envoye');
      setStep(2);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Erreur lors de l'envoi";
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (otp.length !== 6) {
      toast.error('Le code OTP doit contenir 6 chiffres');
      return;
    }

    if (newPassword.length < 6) {
      toast.error('Le mot de passe doit contenir au moins 6 caracteres');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }

    setLoading(true);
    try {
      await resetPassword(email, otp, newPassword);
      toast.success('Mot de passe modifie avec succes!');
      setStep(4);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Code OTP invalide ou expire';
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 to-blue-50 flex flex-col">
      {/* Header */}
      <header className="p-4 md:p-6">
        <Link to="/">
          <Logo />
        </Link>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <Card className="border-0 shadow-xl">
            <CardHeader className="text-center pb-2">
              <CardTitle className="text-2xl font-bold" style={{ fontFamily: 'Plus Jakarta Sans' }}>
                {step === 4 ? 'Mot de passe reinitialise!' : 'Mot de passe oublie'}
              </CardTitle>
              <CardDescription className="text-base">
                {step === 1 && 'Entrez votre adresse email'}
                {step === 2 && 'Entrez le code recu par email et votre nouveau mot de passe'}
                {step === 4 && 'Vous pouvez maintenant vous connecter'}
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-6">
              {/* Step 1: Email */}
              {step === 1 && (
                <form onSubmit={handleSendOTP} className="space-y-5">
                  <div className="space-y-2">
                    <Label htmlFor="email">Adresse email</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                      <Input
                        id="email"
                        type="email"
                        placeholder="email@exemple.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="pl-11 h-12"
                        required
                        data-testid="forgot-email-input"
                      />
                    </div>
                  </div>

                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full h-12 bg-red-500 hover:bg-red-600 text-white font-semibold rounded-full"
                    data-testid="send-otp-button"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Envoi...
                      </>
                    ) : (
                      <>
                        Envoyer le code
                        <ArrowRight className="w-5 h-5 ml-2" />
                      </>
                    )}
                  </Button>
                </form>
              )}

              {/* Step 2: OTP + New Password */}
              {step === 2 && (
                <form onSubmit={handleResetPassword} className="space-y-5">
                  <div className="p-3 bg-slate-50 rounded-lg mb-4">
                    <p className="text-sm text-slate-600">
                      Un code a ete envoye a <span className="font-medium">{email}</span>
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="otp">Code OTP</Label>
                    <Input
                      id="otp"
                      type="text"
                      placeholder="123456"
                      value={otp}
                      onChange={(e) => setOtp(e.target.value.replace(/[^0-9]/g, '').slice(0, 6))}
                      className="h-12 text-center text-2xl tracking-widest"
                      maxLength={6}
                      required
                      data-testid="otp-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="newPassword">Nouveau mot de passe</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                      <Input
                        id="newPassword"
                        type="password"
                        placeholder="Minimum 6 caracteres"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        className="pl-11 h-12"
                        required
                        data-testid="new-password-input"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="confirmPassword">Confirmer le mot de passe</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                      <Input
                        id="confirmPassword"
                        type="password"
                        placeholder="Repetez le mot de passe"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        className="pl-11 h-12"
                        required
                        data-testid="confirm-password-input"
                      />
                    </div>
                  </div>

                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full h-12 bg-red-500 hover:bg-red-600 text-white font-semibold rounded-full"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Modification...
                      </>
                    ) : (
                      'Modifier le mot de passe'
                    )}
                  </Button>

                  <button
                    type="button"
                    onClick={() => setStep(1)}
                    className="w-full text-sm text-slate-500 hover:text-slate-700"
                  >
                    Modifier l'email
                  </button>
                </form>
              )}

              {/* Step 4: Success */}
              {step === 4 && (
                <div className="text-center py-4">
                  <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="w-8 h-8 text-emerald-600" />
                  </div>
                  <Button
                    onClick={() => navigate('/login')}
                    className="w-full h-12 bg-red-500 hover:bg-red-600 text-white font-semibold rounded-full"
                  >
                    Se connecter
                  </Button>
                </div>
              )}

              {step !== 4 && (
                <div className="mt-6 text-center">
                  <Link to="/login" className="text-slate-600 hover:text-red-500">
                    Retour a la connexion
                  </Link>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default ForgotPassword;
